
import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import {
  Grid,
  makeStyles,
  Typography
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import {
  OWNER_TYPES,
  RATING_ROLE,
  STATUS
} from '../../utils/const';
import { addAlert } from '../../actions/alerts.action';
import {
  updateObjectiveStatus,
  updateObjectiveToFailed
} from '../../api/objective/objective.api';
import { fetchObjectiveRates } from '../../api/objective/objectiveRate.api';
import StatusPanel from '../StatusButtons/StatusPanel';
import ObjectiveInfo from './ObjectiveInfo';
import DevelopmentStatus from '../DevelopmentStatus/DevelopmentStatus';
import Todos from '../Todos/TodosPanel';
import CommentsList from '../Comments/CommentsList';
import { FailedObjectiveStatusDialog } from '../Objectives/FailedObjectiveStatusDialog';
import CompletedObjectiveStatusDialog from '../Objectives/CompletedObjectiveStatusDialog';
import ObjectiveKPIs from '../Objectives/kpis/ObjectiveKPIs';
import Attachments from '../Objectives/attachments/Attachments';

const useStyles = makeStyles(theme => ({
  buttons: {
    display: 'flex',
    justifyContent: 'flex-start'
  },
  limitText: {
    display: 'flex',
    justifyContent: 'flex-start',
    color: '#ff0a0a'
  },
  warning: {
    fontSize: '14px'
  }
}));

const DetailsPanel = ({ objective, onSubmitSuccess }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const employeeIdOfCurrentUser = objective?.employee_id;
  const [ closeObjectiveDialogOpen, setCloseObjectiveDialogOpen ] = useState(false);
  const [ completedObjectiveDialogOpen, setCompletedObjectiveDialogOpen ] = useState(false);
  const reachLimitOfActiveObjectives = objective?.owner
    .find(({ type }) => type  === OWNER_TYPES.EMPLOYEE)
    ?.activeObjectiveLimitReached;
  const owner = objective?.owner[0];

  const changeStatusMutation = useMutation(
    async status => {
      return await updateObjectiveStatus({
        objective: {
          ...objective,
          status
        }
      });
    },
    {
      onSuccess: () => {
        onSubmitSuccess();
      },
      onError: error => {
        dispatch(addAlert({ message: error.message, severity: 'error' }));
      }
    }
  );

  const objectiveRatesQuery = useQuery(
    [ 'objectiveRates', objective?.id ],
    async () => {
      const { data } = await fetchObjectiveRates({
        objectiveId: objective?.id
      });
      data.sort((a, b) => a.criteria.criteria_name.hu.localeCompare(b.criteria.criteria_name.hu));
      return data;
    },
    {
      enabled: !!objective,
      onError: error => {
        dispatch(addAlert({ message: error.message, severity: 'error' }));
      }
    }
  );

  const objectiveRates = objectiveRatesQuery.data ?? [];

  const updateObjectiveToFailedMutation = useMutation(
    async () => await updateObjectiveToFailed({
      objectiveData: {
        objective,
        appraiserId: employeeIdOfCurrentUser
      }
    }),
    {
      onSuccess: () => {
        onSubmitSuccess();
      },
      onError: error => {
        dispatch(addAlert({ message: error.message, severity: 'error' }));
      }
    }
  );

  return (
    <>
      <Grid item xs={ 12 } md={ 6 }>
        {owner.type === OWNER_TYPES.EMPLOYEE &&
      <>
        <div className={ classes.buttons }>
          {
            objective.status !== STATUS.COMPLETED &&
            <StatusPanel
              status={ objective.status }
              handleChange={ changeStatusMutation.mutate }
              setCloseObjectiveDialogOpen={ setCloseObjectiveDialogOpen }
              proposedByUser={
                (objective.proposed_by || '').toString() === employeeIdOfCurrentUser
              }
              setCompletedObjectiveDialogOpen={ setCompletedObjectiveDialogOpen }
              reachLimitOfActiveObjectives={ reachLimitOfActiveObjectives }
            />
          }
        </div>
        { reachLimitOfActiveObjectives &&
          objective.status === STATUS.PASSIVE && (
          <div className={ classes.limitText }>
            <Typography
              variant="caption"
              className={ classes.warning }
              gutterBottom
            >
              {t('objective.alert.reachLimit')}
            </Typography>
          </div>
        )}
      </>
        }
        <ObjectiveInfo
          isPanelOpened
          objective = { objective }
          objectiveRates = { objectiveRates }
          averageOfObjectiveRates = { objective.ratingsData.weightedMean }
          onSubmitSuccess={
            () => {
              onSubmitSuccess();
              objectiveRatesQuery.refetch();
            }
          }
        />
      </Grid>
      <Grid item xs={ 12 } md={ 6 }>
        {owner?.type === OWNER_TYPES.EMPLOYEE ?
          <>
            <DevelopmentStatus
              objective={ objective }
              onSubmitSuccess={ onSubmitSuccess }
            />
            <ObjectiveKPIs objective={ objective } />
            <Todos objectiveId={ objective.id } status={ objective.status } />
            <CommentsList
              objectiveId={ objective.objective_id }
              employee={ objective.appraisers_and_criterias.appraisers
                .find(appraiser => appraiser.role === RATING_ROLE.OWNER) }
            />
          </> :
          <ObjectiveKPIs objective={ objective } />
        }
        <Attachments objective={ objective }/>
      </Grid>
      <FailedObjectiveStatusDialog
        closeObjectiveDialogOpen={ closeObjectiveDialogOpen }
        setCloseObjectiveDialogOpen={ setCloseObjectiveDialogOpen }
        handleFailedObjective={ updateObjectiveToFailedMutation.mutate }
      />
      <CompletedObjectiveStatusDialog
        open={ completedObjectiveDialogOpen }
        setCompletedObjectiveDialogOpen={ setCompletedObjectiveDialogOpen }
        handleStatusChange={ changeStatusMutation.mutate }
      />
    </>
  );
};

export default DetailsPanel;